import React from 'react'
import styled from 'styled-components'

/**
 * DashboardCard
 *
 * Shared chrome for dashboard cards: a titled card with an optional icon +
 * subtitle and built-in loading / error / empty states. Wrap any content (a
 * chart, a bar list, a KPI grid) so every dashboard card across roles shares
 * one shell instead of re-implementing the Card + header + state switch.
 *
 * For the common chart/bar compositions use the higher-level `TrendCard` and
 * `BarListCard`, which wire `TrendChart` / `BarList` into this shell.
 *
 * Section cards (a detail page's flat, compact cards) combine the options:
 * `titleAs` puts the title in the page's heading outline, `titleHint` sits
 * right after it (an InfoHint with the card's definition), `size="sm"`,
 * `elevation="flat"` and, for a highlighted card, `surface="tinted"`. Leave
 * them out and the card renders as it always has.
 *
 * `headerLayout="titleBlock"` (STFFLG-4230) is for a header whose right side
 * is a row of controls: the subtitle moves under the title as one block, and
 * `headerRight` sits beside that block, vertically centred and right-aligned,
 * wrapping whole onto its own line when the two no longer fit. The default
 * layout is unchanged.
 *
 * @example
 *   <DashboardCard title="Spend" subtitle="last 6 months" icon={<LineChart size={18} />}
 *     loading={loading} error={error} empty={rows.length === 0}>
 *     <TrendChart data={rows} series={series} xKey="month" />
 *   </DashboardCard>
 *
 * @example a section card
 *   <DashboardCard title="Orders worked" titleAs="h3" size="sm" elevation="flat"
 *     titleHint={<InfoHint label="About Orders worked" content={definition} />}>
 *     …
 *   </DashboardCard>
 *
 * @example a header of controls
 *   <DashboardCard title="Branch comparison" subtitle="Last 30 days" headerLayout="titleBlock"
 *     headerRight={<Stack direction="row" wrap gap="2">…</Stack>}>
 *     …
 *   </DashboardCard>
 */

/** The heading levels a card title can take; the default title is not a heading. */
export type DashboardCardTitleLevel = 'h2' | 'h3' | 'h4'
export type DashboardCardSize = 'md' | 'sm'
export type DashboardCardElevation = 'raised' | 'flat'
export type DashboardCardSurface = 'default' | 'tinted'
export type DashboardCardHeaderLayout = 'default' | 'titleBlock'

const PADDING: Record<DashboardCardSize, string> = {
  md: 'var(--space-6)',
  sm: 'var(--space-4)',
}

const TITLE_SIZE: Record<DashboardCardSize, string> = {
  md: 'var(--fs-subtitle)',
  sm: 'var(--fs-body)',
}

const BODY_GAP: Record<DashboardCardSize, string> = {
  md: 'var(--space-4)',
  sm: 'var(--space-3)',
}

/**
 * The sm card is a flex column whose body takes the spare height and is a flex column too. When a
 * taller neighbour stretches the card, a section can push content to the foot with a `flex: 1`
 * spacer or `margin-top: auto`. md stays block layout.
 */
const CARD_LAYOUT: Record<DashboardCardSize, string> = {
  md: '',
  sm: 'display: flex; flex-direction: column;',
}

const BODY_LAYOUT: Record<DashboardCardSize, string> = {
  md: '',
  sm: 'flex: 1 1 auto; display: flex; flex-direction: column;',
}

const SHADOW: Record<DashboardCardElevation, string> = {
  raised: 'var(--shadow-regular)',
  flat: 'none',
}

const BACKGROUND: Record<DashboardCardSurface, string> = {
  default: 'var(--bg-card)',
  tinted: 'var(--bg-selected)',
}

/** The card's hairlines: the border and the footer rule. On the tint they take its own hue. */
const HAIRLINE: Record<DashboardCardSurface, string> = {
  default: 'var(--border-subtle)',
  tinted: 'var(--brand-200)',
}

/**
 * Subtitle, footer and placeholder ink. --text-secondary is 4.69:1 on the card but about 4.1:1 on
 * --bg-selected (below AA for small text), so the tinted surface uses --text-primary.
 */
const MUTED_INK: Record<DashboardCardSurface, string> = {
  default: 'var(--text-secondary)',
  tinted: 'var(--text-primary)',
}

interface CardStyleProps {
  $size: DashboardCardSize
  $elevation: DashboardCardElevation
  $surface: DashboardCardSurface
}

const Card = styled.div<CardStyleProps>`
  background: ${({$surface}) => BACKGROUND[$surface]};
  border: var(--border-thin) solid ${({$surface}) => HAIRLINE[$surface]};
  border-radius: var(--radius-md);
  box-shadow: ${({$elevation}) => SHADOW[$elevation]};
  padding: ${({$size}) => PADDING[$size]};
  box-sizing: border-box;
  /* Fill the grid cell so cards sharing a row are equal height. */
  height: 100%;
  ${({$size}) => CARD_LAYOUT[$size]}
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
`

/** Holds `titleHint` beside the title. */
const HintSlot = styled.span`
  display: inline-flex;
  align-items: center;
`

const TitleGroup = styled.div<{$size: DashboardCardSize}>`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: ${({$size}) => TITLE_SIZE[$size]};
  font-weight: var(--fw-bold);
  color: var(--primary-800);
  letter-spacing: -0.015em;

  svg {
    color: var(--brand-cta);
  }

  /* The brand tint is for the leading icon; a hint's icon keeps the ink its trigger sets. */
  ${HintSlot} svg {
    color: inherit;
  }
`

/** The title as a heading: the level places it in the page outline, the look stays the title's. */
const Title = styled.div`
  margin: 0;
  font: inherit;
  color: inherit;
  letter-spacing: inherit;
`

const Sub = styled.div<{$surface: DashboardCardSurface}>`
  font-size: var(--fs-caption);
  color: ${({$surface}) => MUTED_INK[$surface]};
  margin-top: var(--space-1);
`

/** `headerLayout="titleBlock"`: the title row over its subtitle, one flex item of the header. */
const TitleBlock = styled.div`
  min-width: 0;
`

/**
 * The `titleBlock` header (STFFLG-4230). `headerRight` renders straight into it, not wrapped in a
 * DS element, as its next flex item: centred on the title block (`align-items`), pushed to the
 * end by its auto start margin, and, once the two no longer fit, wrapped whole onto a line of its
 * own where the margin keeps it right-aligned. Being the header's own item, a caller's
 * `flex-basis: 100%` on it resolves against the header and gives it the full row.
 */
const TitleBlockHeader = styled(Header)`
  flex-wrap: wrap;
  row-gap: var(--space-3);

  & > :not(${TitleBlock}) {
    margin-inline-start: auto;
    max-width: 100%;
  }
`

const Body = styled.div<{$size: DashboardCardSize}>`
  margin-top: ${({$size}) => BODY_GAP[$size]};
  ${({$size}) => BODY_LAYOUT[$size]}
`

const State = styled.div<{$minHeight: string; $surface: DashboardCardSurface}>`
  min-height: ${({$minHeight}) => $minHeight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({$surface}) => MUTED_INK[$surface]};
  font-size: var(--fs-body);
`

const Footer = styled.div<{$surface: DashboardCardSurface}>`
  margin-top: var(--space-4);
  padding-top: var(--space-3);
  border-top: var(--border-thin) solid ${({$surface}) => HAIRLINE[$surface]};
  font-size: var(--fs-caption);
  color: ${({$surface}) => MUTED_INK[$surface]};
`

export interface DashboardCardProps {
  /** Card title (rendered next to the optional icon). */
  title: string
  /**
   * Render the title as a heading at this level so the card sits in the page's heading outline.
   * It looks the same either way. Default: plain title text, not a heading.
   */
  titleAs?: DashboardCardTitleLevel
  /**
   * A node right after the title, outside the heading, e.g. an InfoHint with the card's
   * definition. It keeps its own colour: the brand tint applies to `icon` only.
   */
  titleHint?: React.ReactNode
  /** Optional caption under the title. */
  subtitle?: string
  /** Optional leading icon (e.g. a lucide icon element); tinted `--brand-cta`. */
  icon?: React.ReactNode
  /** Show the loading placeholder instead of children. */
  loading?: boolean
  /** Show the error placeholder instead of children (truthy = error). */
  error?: Error | null | boolean
  /** Show the empty placeholder instead of children. */
  empty?: boolean
  loadingLabel?: string
  errorLabel?: string
  emptyLabel?: string
  /** Optional node rendered on the right of the header (badge, count, link, filter). */
  headerRight?: React.ReactNode
  /**
   * `'default'`: the title row with `headerRight` at its end, and the subtitle under the whole row.
   * `'titleBlock'`: the subtitle sits under the title as one block, and `headerRight` (one node,
   * e.g. a group of controls) is the header's next flex item: vertically centred on the block,
   * right-aligned, and wrapped whole onto its own line, still right-aligned, when the two no longer
   * fit. Its `flex-basis` resolves against the header, so `flex: 1 1 100%` gives it a full row.
   */
  headerLayout?: DashboardCardHeaderLayout
  /** Optional footer; persists across loading / error / empty states. */
  footer?: React.ReactNode
  /** Min-height of the placeholder state area (match your content height). */
  minHeight?: string
  /**
   * `'md'` (default): `--space-6` padding and a `--fs-subtitle` title. `'sm'`: the compact section
   * card, `--space-4` padding, a `--fs-body` title and a `--space-3` gap above the body. The sm
   * card and its body are flex columns and the body takes the card's spare height, so a child
   * `flex: 1` spacer or `margin-top: auto` pins content to the foot of a stretched card.
   */
  size?: DashboardCardSize
  /** `'raised'` (default): the card shadow. `'flat'`: no shadow, only the hairline border. */
  elevation?: DashboardCardElevation
  /**
   * `'default'`: `--bg-card`. `'tinted'`: `--bg-selected` with a `--brand-200` border and footer
   * rule; the subtitle, footer and placeholder text move to `--text-primary` to keep AA contrast.
   */
  surface?: DashboardCardSurface
  children: React.ReactNode
  'data-testid'?: string
}

export function DashboardCard({
  title,
  titleAs,
  titleHint,
  subtitle,
  icon,
  loading,
  error,
  empty,
  loadingLabel = 'Loading…',
  errorLabel = 'Failed to load',
  emptyLabel = 'No data available',
  headerRight,
  headerLayout = 'default',
  footer,
  minHeight = '160px',
  size = 'md',
  elevation = 'raised',
  surface = 'default',
  children,
  'data-testid': dataTestId,
}: DashboardCardProps) {
  const titleGroup = (
    <TitleGroup $size={size}>
      {icon}
      {titleAs ? <Title as={titleAs}>{title}</Title> : title}
      {titleHint ? <HintSlot>{titleHint}</HintSlot> : null}
    </TitleGroup>
  )
  const sub = subtitle ? <Sub $surface={surface}>{subtitle}</Sub> : null

  return (
    <Card $size={size} $elevation={elevation} $surface={surface} data-testid={dataTestId}>
      {headerLayout === 'titleBlock' ? (
        <TitleBlockHeader>
          <TitleBlock>
            {titleGroup}
            {sub}
          </TitleBlock>
          {headerRight}
        </TitleBlockHeader>
      ) : (
        <>
          <Header>
            {titleGroup}
            {headerRight}
          </Header>
          {sub}
        </>
      )}
      <Body $size={size}>
        {loading ? (
          <State $minHeight={minHeight} $surface={surface}>
            {loadingLabel}
          </State>
        ) : error ? (
          <State $minHeight={minHeight} $surface={surface}>
            {errorLabel}
          </State>
        ) : empty ? (
          <State $minHeight={minHeight} $surface={surface}>
            {emptyLabel}
          </State>
        ) : (
          children
        )}
      </Body>
      {footer ? <Footer $surface={surface}>{footer}</Footer> : null}
    </Card>
  )
}
